// Fetch-based SSE client for `GET /api/runs/:runId/stream` (dash-api's `AiRunController`, shared
// by both the legacy classifier-chat run vocabulary and the newer agentic-loop vocabulary — see
// that controller's doc comment; there is no `?engine=` switch, the same endpoint just carries
// whichever event names the run in question actually emits).
//
// Why not a native `EventSource`: native `EventSource` cannot attach custom request headers, so
// it has no way to send the `Authorization: Bearer <jwt>` header this endpoint requires. The
// existing SSE code in this same feature folder (`../sse/runStream.js`, `../sse/conversationStream.js`)
// uses plain, unauthenticated `new EventSource(url)` against this same dash-api backend — those
// endpoints apparently don't enforce auth today, so that gap never had to be solved there. Rather
// than inventing something bespoke, this module follows the one existing precedent in this
// codebase for authenticated fetch-based streaming — `src/utils/llmStreamingApi.js`
// (`fetch()` + `response.body.getReader()` + `TextDecoder` + `AbortController`/`signal`) — adapted
// to parse SSE `event:`/`data:` frames instead of that module's brace-counting JSON scanner.
//
// Reconnect/retry (ticket 13): native `EventSource` reconnects automatically on a dropped
// connection; this hand-rolled client previously had no equivalent, so a network blip mid-stream
// silently ended the run as failed from the UI's perspective even though the backend run kept
// going. `../sse/runStream.js`/`conversationStream.js` don't implement any manual reconnect logic
// either -- they just lean on EventSource's native behavior -- so there is no existing bounded-retry
// convention in this codebase to mirror; the scheme below (`DEFAULT_MAX_RETRIES` attempts,
// doubling backoff starting at `DEFAULT_RETRY_DELAY_MS`) is a new, deliberately simple one, and
// both constants are overridable per call for tests.
import {getAwpClientHeaders} from '../../../../session/awpRequestHeaders';
import {buildAgenticRunStreamUrl} from './agenticRunApi';

/** Parses one `event: <type>\ndata: <json>\n...` SSE frame (already split off the `\n\n`
 * terminator by the caller) into `{eventName, payload}`. Multiple `data:` lines are joined with
 * `\n` per the SSE spec, even though this backend never actually splits `data:` across lines
 * today. */
export function parseSseFrame(frame) {
    let eventName = 'message';
    const dataLines = [];

    frame.split('\n').forEach((line) => {
        if (line.startsWith('event:')) {
            eventName = line.slice('event:'.length).trim();
        } else if (line.startsWith('data:')) {
            dataLines.push(line.slice('data:'.length).replace(/^ /, ''));
        }
    });

    const rawData = dataLines.join('\n');
    let payload = {};
    if (rawData) {
        try {
            payload = JSON.parse(rawData);
        } catch (_) {
            payload = {message: rawData};
        }
    }

    return {eventName, payload};
}

/** Total number of reconnect attempts allowed for one dropped connection over the lifetime of a
 * single `streamAgenticRun` call (NOT reset on a successful reconnect) -- a hard ceiling rather
 * than a per-drop budget, so a connection that keeps flapping still gives up instead of retrying
 * forever. */
const DEFAULT_MAX_RETRIES = 3;
/** Base backoff delay; attempt N waits `DEFAULT_RETRY_DELAY_MS * 2^(N-1)` ms (500, 1000, 2000…). */
const DEFAULT_RETRY_DELAY_MS = 500;

function isRetryableStreamStatus(status) {
    return status === 502 || status === 503 || status === 504;
}

/**
 * Opens an authenticated SSE connection to `GET /api/runs/:runId/stream` and invokes `handlers` as
 * frames arrive. Automatically retries (bounded, with backoff) if the connection drops
 * unexpectedly mid-stream after having successfully opened -- a clean end-of-stream (the backend
 * closing the emitter once the run finishes) still calls `onEnd` exactly once and never retries.
 * An initial 401/403/404 is NOT retried -- that's "the run never started streaming"
 * (bad auth, unknown runId). Transient open failures (`Failed to fetch`, 502/503/504) ARE
 * retried: remote proxies often kill a long SSE before the first named event.
 *
 * Returns an unsubscribe function that aborts the underlying fetch and cancels any pending retry.
 *
 * @param {string} runId
 * @param {{
 *   apiBaseUrl?: string,
 *   onOpen?: () => void,
 *   onEvent?: (event: {eventName: string, payload: unknown}) => void,
 *   onError?: (error: Error) => void,
 *   onEnd?: () => void,
 *   maxRetries?: number,
 *   retryDelayMs?: number,
 * }} [handlers]
 * @returns {() => void} unsubscribe
 */
export function streamAgenticRun(runId, handlers = {}) {
    const {
        apiBaseUrl,
        onOpen,
        onEvent,
        onError,
        onEnd,
        maxRetries = DEFAULT_MAX_RETRIES,
        retryDelayMs = DEFAULT_RETRY_DELAY_MS,
    } = handlers;

    const controller = new AbortController();
    let closed = false;
    let retryCount = 0;
    let retryTimeoutId = null;

    const url = buildAgenticRunStreamUrl(runId, apiBaseUrl);

    function scheduleRetry(error) {
        if (closed) {
            return;
        }
        if (retryCount >= maxRetries) {
            onError?.(error);
            return;
        }
        retryCount += 1;
        const delay = retryDelayMs * 2 ** (retryCount - 1);
        retryTimeoutId = setTimeout(() => {
            retryTimeoutId = null;
            if (!closed) {
                connect();
            }
        }, delay);
    }

    function connect() {
        (async () => {
            let response;
            try {
                response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        Accept: 'text/event-stream',
                        ...getAwpClientHeaders(),
                    },
                    signal: controller.signal,
                });
            } catch (error) {
                if (closed || error?.name === 'AbortError') {
                    return;
                }
                // Remote proxies often surface idle/gzip kills as TypeError: Failed to fetch
                // before any status line. Retry those; do not retry a user abort.
                scheduleRetry(error);
                return;
            }

            if (closed) {
                return;
            }

            if (!response.ok || !response.body?.getReader) {
                const openError = new Error(`Failed to open agentic run stream (${response.status})`);
                if (isRetryableStreamStatus(response.status)) {
                    scheduleRetry(openError);
                    return;
                }
                onError?.(openError);
                return;
            }

            onOpen?.();

            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let midStreamError = null;

            try {
                while (!closed) {
                    // eslint-disable-next-line no-await-in-loop
                    const {value, done} = await reader.read();
                    if (done) {
                        break;
                    }

                    buffer += decoder.decode(value, {stream: true});

                    let frameBreak = buffer.indexOf('\n\n');
                    while (frameBreak !== -1) {
                        const frame = buffer.slice(0, frameBreak);
                        buffer = buffer.slice(frameBreak + 2);
                        if (frame.trim()) {
                            onEvent?.(parseSseFrame(frame));
                        }
                        frameBreak = buffer.indexOf('\n\n');
                    }
                }
            } catch (error) {
                midStreamError = error;
            } finally {
                try {
                    reader.releaseLock();
                } catch (_) {
                    // already released/cancelled
                }
            }

            if (closed) {
                return;
            }

            if (midStreamError) {
                // The stream opened successfully (onOpen already fired) and then broke -- this IS
                // the "unexpected drop" case the ticket asks to retry, as opposed to the
                // clean-`done`-without-throwing path below.
                scheduleRetry(midStreamError);
                return;
            }

            onEnd?.();
        })();
    }

    connect();

    return () => {
        if (closed) {
            return;
        }
        closed = true;
        if (retryTimeoutId !== null) {
            clearTimeout(retryTimeoutId);
            retryTimeoutId = null;
        }
        controller.abort();
    };
}
